import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../store/useAppStore';
import { FileText, Loader2, CheckCircle, AlertCircle, Trash2, ArrowRight, Plus, X, Search, Microscope, Download, Layers, Edit, Save, CheckCircle2, Square, CheckSquare, Tag, Sliders, MapPin, ZoomIn, RefreshCw, Crosshair, HelpCircle, Pointer, MousePointer2, Navigation } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn, formatDate, formatFileSize, generateId } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { HistologyImage, ImageAnnotation, ImageFilters } from '../types';
import { motion, AnimatePresence } from 'framer-motion';

const StatusBadge = ({ status }: { status: string }) => {
  const styles = {
    uploaded: "bg-slate-100 text-slate-600",
    processing: "bg-blue-100 text-blue-700",
    processed: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700",
  };
  
  const icons = {
    uploaded: null,
    processing: <Loader2 className="h-3 w-3 mr-1 animate-spin" />,
    processed: <CheckCircle className="h-3 w-3 mr-1" />,
    failed: <AlertCircle className="h-3 w-3 mr-1" />,
  };

  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider",
      (styles as any)[status] || styles.uploaded
    )}>
      {(icons as any)[status]}
      {status}
    </span>
  );
};

const SlideModal = ({ image, onClose }: { image: HistologyImage, onClose: () => void }) => {
  const updateImageMetadata = useAppStore(state => state.updateImageMetadata);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLabel, setEditedLabel] = useState(image.identified_organ_tissue || '');
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'stain' | 'pins'>('info');
  
  // Annotation State
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [namingPin, setNamingPin] = useState<{ x: number, y: number } | null>(null);
  const [pinNameInput, setPinNameInput] = useState('');
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Filters Local State
  const [filters, setFilters] = useState<ImageFilters>(image.filters || { brightness: 100, contrast: 100, grayscale: false, inverted: false });

  // Mouse/Pin Placement Tracking
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, show: false });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!imageContainerRef.current) return;
    const { left, top, width, height } = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setMousePos({ x, y, show: true });
  };

  const handlePlacePin = (e: React.MouseEvent) => {
    if (!isAddingPin || !imageContainerRef.current) return;
    e.stopPropagation();

    const { left, top, width, height } = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setNamingPin({ x, y });
    setPinNameInput('');
  };

  const confirmAddPin = async () => {
    if (!namingPin || !pinNameInput.trim()) return;

    const newAnnotation: ImageAnnotation = {
      id: generateId(),
      label: pinNameInput.trim(),
      x_percent: namingPin.x,
      y_percent: namingPin.y
    };

    const updatedAnnotations = [...(image.annotations || []), newAnnotation];
    await updateImageMetadata(image.id, { annotations: updatedAnnotations });
    setNamingPin(null);
    setIsAddingPin(false);
  };

  const deletePin = async (pinId: string) => {
    const updated = (image.annotations || []).filter(p => p.id !== pinId);
    await updateImageMetadata(image.id, { annotations: updated });
  };

  const updateFilters = async (newFilters: Partial<ImageFilters>) => {
    const fullFilters = { ...filters, ...newFilters };
    setFilters(fullFilters);
    await updateImageMetadata(image.id, { filters: fullFilters });
  };

  const handleSaveLabel = async () => {
    if (editedLabel.trim()) {
      setIsSyncing(true);
      try {
        await updateImageMetadata(image.id, { identified_organ_tissue: editedLabel.trim() });
        setIsEditing(false);
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const filterStyle = {
    filter: `brightness(${filters.brightness}%) contrast(${filters.contrast}%) grayscale(${filters.grayscale ? 1 : 0}) invert(${filters.inverted ? 1 : 0})`
  };

  // Clinical Arrow Component
  const ClinicalArrow = ({ color = "#3B82F6", size = 32 }: { color?: string, size?: number }) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
    >
      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 17L17 7M17 7H7M17 7V17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur-xl flex items-center justify-center p-4 md:p-12"
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white w-full max-w-6xl rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col md:flex-row h-full max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* VIEWPORT */}
        <div 
          ref={imageContainerRef}
          className={cn(
            "flex-1 bg-slate-950 flex items-center justify-center relative group min-h-[300px] md:min-h-0 overflow-hidden",
            isAddingPin ? "cursor-none" : "cursor-crosshair"
          )}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setMousePos(prev => ({ ...prev, show: false }))}
        >
          {/* Interaction Capture Layer for Pinning */}
          {isAddingPin && !namingPin && (
            <div 
              className="absolute inset-0 z-[40] cursor-none bg-primary-500/5" 
              onClick={handlePlacePin}
            />
          )}

          <img 
            src={image.image_url} 
            alt={image.identified_organ_tissue} 
            className="w-full h-full object-contain p-2 transition-all duration-300"
            style={filterStyle}
          />

          {/* Active Interaction HUD */}
          <AnimatePresence>
            {isAddingPin && !namingPin && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="absolute top-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-primary-600 text-white rounded-full shadow-2xl flex items-center gap-3 border border-white/20 whitespace-nowrap"
              >
                <Crosshair className="h-4 w-4 animate-spin-slow" />
                <span className="text-xs font-black uppercase tracking-widest">TAP TO MARK STRUCTURE</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Ghost Follower (Clinical Arrow) */}
          {isAddingPin && !namingPin && mousePos.show && (
            <div 
              className="absolute pointer-events-none z-50 transition-none"
              style={{ left: `${mousePos.x}%`, top: `${mousePos.y}%` }}
            >
               <div className="relative flex flex-col items-center">
                  <div className="-translate-x-[10%] -translate-y-[90%]">
                    <ClinicalArrow color="#3B82F6" size={48} />
                  </div>
                  <div className="absolute top-2 left-4 bg-primary-600 px-3 py-1 rounded-lg text-[9px] font-black text-white uppercase tracking-widest shadow-2xl border border-white/20 whitespace-nowrap">
                    IDENTIFY POINT
                  </div>
               </div>
            </div>
          )}

          {/* Existing Annotations Layer (Arrows) */}
          <AnimatePresence>
            {(image.annotations || []).map(pin => (
              <motion.div 
                key={pin.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute z-20 group/pin"
                style={{ left: `${pin.x_percent}%`, top: `${pin.y_percent}%` }}
              >
                <div className="relative -translate-x-[10%] -translate-y-[90%]">
                  <ClinicalArrow color="#3B82F6" size={32} />
                  <div className="absolute top-0 left-6 bg-slate-900/90 text-white text-[10px] font-black px-3 py-1 rounded-full whitespace-nowrap opacity-0 group-hover/pin:opacity-100 transition-all z-[60] shadow-xl">
                    {pin.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Pin Naming Dialog Overlay */}
          <AnimatePresence>
            {namingPin && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                className="absolute inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-6"
              >
                <div className="bg-white p-8 rounded-[2rem] w-full max-w-sm shadow-2xl border border-slate-100" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-primary-50 p-2.5 rounded-xl"><Tag className="h-5 w-5 text-primary-500" /></div>
                    <h4 className="text-xl font-black text-slate-900 uppercase tracking-tight">Anatomical Label</h4>
                  </div>
                  <input 
                    autoFocus
                    type="text"
                    value={pinNameInput}
                    onChange={e => setPinNameInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && confirmAddPin()}
                    placeholder="e.g. Simple Cuboidal Cell..."
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-primary-500 outline-none font-bold text-slate-900 mb-6 placeholder:text-slate-300"
                  />
                  <div className="flex flex-col gap-3">
                    <Button onClick={confirmAddPin} disabled={!pinNameInput.trim()} className="w-full h-14 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-primary-500/20">
                      Save Quiz Point
                    </Button>
                    <Button variant="ghost" onClick={() => setNamingPin(null)} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      Cancel
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Zoom Magnifier (Only when not pinning) */}
          {mousePos.show && activeTab === 'info' && !isAddingPin && (
            <div 
              className="absolute w-40 h-40 border-4 border-white rounded-full shadow-2xl pointer-events-none z-50 overflow-hidden hidden md:block"
              style={{ 
                left: `calc(${mousePos.x}% - 80px)`, 
                top: `calc(${mousePos.y}% - 80px)`,
                backgroundImage: `url(${image.image_url})`,
                backgroundSize: '800%',
                backgroundPosition: `${mousePos.x}% ${mousePos.y}%`,
                filter: filterStyle.filter
              }}
            />
          )}

          <div className="absolute top-4 left-4 flex flex-col gap-2">
              <div className="bg-slate-900/60 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/10 text-[9px] font-black uppercase tracking-widest text-white shadow-sm flex items-center">
                  <Microscope className="h-3 w-3 mr-2 text-primary-400" />
                  {isAddingPin ? "ANATOMICAL MAPPING" : "SCANNER VIEW"}
              </div>
          </div>
          
          <button onClick={onClose} className="md:hidden absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-md rounded-xl shadow-sm z-10">
              <X className="h-5 w-5 text-white" />
          </button>
        </div>
        
        {/* SIDEBAR */}
        <div className="w-full md:w-[24rem] flex flex-col bg-white border-l border-slate-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            <button 
              onClick={() => { setActiveTab('info'); setIsAddingPin(false); setNamingPin(null); }}
              className={cn("flex-1 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all", activeTab === 'info' ? "border-primary-500 text-primary-600" : "border-transparent text-slate-400")}
            >
              Info
            </button>
            <button 
              onClick={() => { setActiveTab('stain'); setIsAddingPin(false); setNamingPin(null); }}
              className={cn("flex-1 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all", activeTab === 'stain' ? "border-primary-500 text-primary-600" : "border-transparent text-slate-400")}
            >
              Stain Lab
            </button>
            <button 
              onClick={() => { setActiveTab('pins'); setNamingPin(null); }}
              className={cn("flex-1 py-4 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all", activeTab === 'pins' ? "border-primary-500 text-primary-600" : "border-transparent text-slate-400")}
            >
              Arrows ({image.annotations?.length || 0})
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-8">
            {activeTab === 'info' && (
              <>
                <div className="space-y-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Main Classification</p>
                    {isEditing ? (
                      <div className="space-y-3">
                        <input 
                          autoFocus
                          className="w-full text-xl font-black bg-slate-50 border-b-2 border-primary-500 outline-none py-2"
                          value={editedLabel}
                          onChange={(e) => setEditedLabel(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveLabel} isLoading={isSyncing} className="rounded-lg px-4 font-black uppercase tracking-widest h-9">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="rounded-lg px-4 font-bold text-slate-400 uppercase tracking-widest h-9">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight">{image.identified_organ_tissue}</h2>
                        <button onClick={() => setIsEditing(true)} className="flex items-center text-[10px] font-black text-primary-600 uppercase tracking-widest hover:translate-x-1 transition-transform">
                          <Edit className="h-3 w-3 mr-2" /> Rename Slide
                        </button>
                      </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Metadata</p>
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-slate-700 truncate flex items-center">
                            <FileText className="h-3 w-3 mr-2 text-slate-400" />
                            {image.original_filename}
                          </p>
                          <p className="text-[10px] text-slate-400 font-medium">Captured {formatDate(image.created_at)}</p>
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-100 p-4 rounded-2xl flex items-start gap-3">
                      <div className="bg-white p-1.5 rounded-lg shadow-sm"><ZoomIn className="h-4 w-4 text-amber-600" /></div>
                      <div>
                        <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Diagnostic Tip</p>
                        <p className="text-[10px] text-amber-700/80 font-medium mt-1">Magnify structural boundaries to verify identification before starting quiz.</p>
                      </div>
                    </div>
                </div>
              </>
            )}

            {activeTab === 'stain' && (
              <div className="space-y-8">
                <div className="space-y-6">
                  <div>
                    <label className="flex justify-between text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
                      <span>Brightness</span>
                      <span>{filters.brightness}%</span>
                    </label>
                    <input 
                      type="range" min="50" max="150" value={filters.brightness} 
                      onChange={(e) => updateFilters({ brightness: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-primary-500"
                    />
                  </div>
                  <div>
                    <label className="flex justify-between text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">
                      <span>Contrast</span>
                      <span>{filters.contrast}%</span>
                    </label>
                    <input 
                      type="range" min="50" max="150" value={filters.contrast} 
                      onChange={(e) => updateFilters({ contrast: parseInt(e.target.value) })}
                      className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => updateFilters({ grayscale: !filters.grayscale })}
                    className={cn(
                      "flex flex-col items-center p-4 rounded-2xl border transition-all gap-2",
                      filters.grayscale ? "bg-primary-50 border-primary-200 text-primary-600" : "bg-white border-slate-100 text-slate-400"
                    )}
                  >
                    <Sliders className="h-5 w-5" />
                    <span className="text-[9px] font-black uppercase tracking-widest">Morphology Only</span>
                  </button>
                  <button 
                    onClick={() => updateFilters({ inverted: !filters.inverted })}
                    className={cn(
                      "flex flex-col items-center p-4 rounded-2xl border transition-all gap-2",
                      filters.inverted ? "bg-indigo-50 border-indigo-200 text-indigo-600" : "bg-white border-slate-100 text-slate-400"
                    )}
                  >
                    <RefreshCw className="h-5 w-5" />
                    <span className="text-[9px] font-black uppercase tracking-widest">Invert View</span>
                  </button>
                </div>

                <Button variant="ghost" onClick={() => updateFilters({ brightness: 100, contrast: 100, grayscale: false, inverted: false })} className="w-full h-11 text-[9px] font-black uppercase tracking-widest text-slate-400">
                  Restore Clinical Default
                </Button>
              </div>
            )}

            {activeTab === 'pins' && (
              <div className="space-y-6">
                <Button 
                  onClick={(e) => { e.stopPropagation(); setIsAddingPin(!isAddingPin); setNamingPin(null); }}
                  className={cn(
                    "w-full h-14 rounded-2xl font-black uppercase tracking-widest text-xs",
                    isAddingPin ? "bg-red-500 hover:bg-red-600" : "bg-primary-600 hover:bg-primary-700 shadow-xl shadow-primary-500/20"
                  )}
                >
                  {isAddingPin ? <X className="h-4 w-4 mr-2" /> : <Navigation className="h-4 w-4 mr-2 rotate-45" />}
                  {isAddingPin ? "Cancel Marking" : "Add Anatomical Arrow"}
                </Button>

                <div className="space-y-3">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Active Structures</p>
                  {(image.annotations || []).length === 0 ? (
                    <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 flex flex-col items-center gap-3">
                      <MousePointer2 className="h-6 w-6 text-slate-300" />
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No structures marked yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(image.annotations || []).map(pin => (
                        <div key={pin.id} className="group flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl hover:border-primary-200 transition-all">
                          <div className="flex items-center gap-3">
                            <Navigation className="h-3 w-3 text-primary-500 rotate-45" />
                            <span className="text-xs font-bold text-slate-700">{pin.label}</span>
                          </div>
                          <button onClick={() => deletePin(pin.id)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 sm:p-8 pt-0 border-t border-slate-50">
            <Link to={`/practice/${image.pdf_id}`} className="block">
              <Button className="w-full h-14 rounded-2xl shadow-xl shadow-indigo-500/20 text-xs font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-700">
                <Layers className="h-4 w-4 mr-2" /> Study Slide Deck
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const BulkEditModal = ({ selectedCount, onSave, onClose }: { selectedCount: number, onSave: (label: string) => void, onClose: () => void }) => {
  const [label, setLabel] = useState('');

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white w-full max-w-md rounded-[2rem] p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="space-y-1">
            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Batch Rename</h3>
            <p className="text-slate-500 text-xs font-medium">Updating {selectedCount} selected slides.</p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-50 hover:bg-slate-100 rounded-xl">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">New Identification Label</label>
            <div className="relative">
              <Tag className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary-500" />
              <input 
                autoFocus
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-primary-500 outline-none font-bold text-slate-900 placeholder:text-slate-300"
                placeholder="e.g. Lung Tissue (H&E)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && label.trim() && onSave(label.trim())}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => onSave(label.trim())} 
              disabled={!label.trim()}
              className="w-full h-14 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-primary-500/20"
            >
              Update All Selected
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const LibraryPage = () => {
  const { pdfs, images, deleteImages, bulkUpdateLabels } = useAppStore();
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const navigate = useNavigate();

  const selectedImage = images.find(img => img.id === selectedImageId);

  const toggleImageSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (confirm(`Are you sure you want to delete ${selectedIds.size} slides from your collection?`)) {
      await deleteImages(Array.from(selectedIds));
      setSelectedIds(new Set());
      setIsSelectionMode(false);
    }
  };

  const handleBulkPractice = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds).join(',');
    navigate(`/practice?ids=${ids}`);
  };

  const handleBulkUpdate = async (label: string) => {
    await bulkUpdateLabels(Array.from(selectedIds), label);
    setIsBulkEditModalOpen(false);
    setIsSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleSelectionMode = () => {
    if (isSelectionMode) {
      setSelectedIds(new Set());
    }
    setIsSelectionMode(!isSelectionMode);
  };

  return (
    <div className="space-y-8 sm:space-y-12 px-1 pb-32 md:px-0 relative">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-4 sm:pt-8 pb-4">
        <div className="space-y-1 sm:space-y-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Slide Bank</h1>
          <p className="text-slate-500 text-sm sm:text-base md:text-lg max-w-xl">
            Browse your histology collection and manage anatomical annotations.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 sm:pt-0">
          <Button 
            variant={isSelectionMode ? "primary" : "outline"} 
            onClick={handleToggleSelectionMode} 
            className="flex-1 sm:flex-none h-11 sm:h-12 rounded-full px-5 sm:px-6 font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap"
          >
            {isSelectionMode ? <X className="h-4 w-4 mr-2" /> : <CheckSquare className="h-4 w-4 mr-2" />}
            {isSelectionMode ? "Exit Selection" : "Bulk Actions"}
          </Button>
          <Link to="/upload" className="flex-1 sm:flex-none">
             <Button className="w-full sm:w-auto h-11 sm:h-12 shadow-xl shadow-primary-500/20 px-6 sm:px-8 font-bold text-[10px] sm:text-xs uppercase tracking-widest rounded-full" variant="primary">
               <Plus className="h-4 w-4 mr-2" />
               Extract More
             </Button>
          </Link>
        </div>
      </div>

      <AnimatePresence>
        {isSelectionMode && selectedIds.size > 0 && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-0 right-0 px-4 sm:px-6 z-[80] flex justify-center"
          >
            <div className="bg-slate-900 text-white p-3 sm:p-4 rounded-3xl shadow-2xl flex flex-col md:flex-row items-center justify-between gap-4 border border-white/10 backdrop-blur-xl w-full max-w-4xl">
              <div className="flex items-center gap-3 self-start md:self-auto ml-2">
                <div className="bg-primary-500 p-2 rounded-xl">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest">{selectedIds.size} Selected</p>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar justify-between sm:justify-end">
                <Button 
                  onClick={handleBulkDelete}
                  variant="danger" 
                  className="h-10 sm:h-12 flex-1 sm:flex-none rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest px-4 sm:px-6"
                >
                  <Trash2 className="h-4 w-4 mr-1 sm:mr-2" /> Delete
                </Button>
                <Button 
                  onClick={() => setIsBulkEditModalOpen(true)}
                  className="h-10 sm:h-12 flex-1 sm:flex-none rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest px-4 sm:px-6 bg-indigo-600 hover:bg-indigo-500"
                >
                  <Edit className="h-4 w-4 mr-1 sm:mr-2" /> Labels
                </Button>
                <Button 
                  onClick={handleBulkPractice}
                  className="h-10 sm:h-12 flex-1 sm:flex-none rounded-xl text-[9px] sm:text-[10px] font-black uppercase tracking-widest px-4 sm:px-6 bg-emerald-600 hover:bg-emerald-500"
                >
                  <Microscope className="h-4 w-4 mr-1 sm:mr-2" /> Practice
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wider">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              Source Materials
            </h3>
        </div>
        {pdfs.length === 0 ? (
            <div className="p-16 sm:p-20 text-center">
              <div className="bg-slate-50 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-slate-300" />
              </div>
              <p className="text-slate-500 text-base sm:text-lg font-medium">No documents processed yet.</p>
              <Link to="/upload" className="text-primary-600 hover:underline mt-2 inline-block font-semibold text-sm">
                Extract your first slide
              </Link>
            </div>
        ) : (
            <div className="divide-y divide-slate-100">
                {pdfs.map((pdf) => (
                    <div key={pdf.id} className="p-5 sm:p-6 md:p-8 flex flex-col gap-5 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start justify-between sm:justify-start gap-4">
                            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100">
                                <FileText className="h-6 w-6 sm:h-7 sm:w-7 text-red-500" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h4 className="font-bold text-slate-900 text-base sm:text-lg truncate max-w-full">{pdf.filename}</h4>
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-[10px] sm:text-sm text-slate-500 font-medium">
                                    <span>{formatFileSize(pdf.size_mb * 1024 * 1024)}</span>
                                    <span className="text-slate-300 hidden sm:inline">•</span>
                                    <span className="whitespace-nowrap">{formatDate(pdf.uploaded_at)}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                            <StatusBadge status={pdf.status} />
                            
                            {pdf.status === 'processed' && (
                                <div className="flex gap-2 flex-1 sm:flex-none justify-end">
                                  <Link to={`/flashcards/${pdf.id}`} className="flex-1 sm:flex-none">
                                      <Button size="sm" variant="ghost" className="w-full sm:w-auto h-10 rounded-full px-4 text-primary-600 font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap">
                                        Flashcards
                                      </Button>
                                  </Link>
                                  <Link to={`/practice/${pdf.id}`} className="flex-1 sm:flex-none">
                                      <Button size="sm" variant="outline" className="w-full sm:w-auto h-10 rounded-full px-5 sm:px-6 border-slate-300 font-bold text-[10px] sm:text-xs uppercase tracking-widest whitespace-nowrap">
                                        Review Session <ArrowRight className="ml-1.5 h-3 w-3" />
                                      </Button>
                                  </Link>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )}
      </div>

      <div className="space-y-6 sm:space-y-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-3">
            Slide Gallery
            <span className="bg-primary-50 text-primary-600 text-xs sm:text-sm px-3 py-1 rounded-full font-bold">
              {images.length}
            </span>
          </h3>
          {isSelectionMode && (
             <div className="hidden sm:flex text-[10px] font-black text-primary-600 uppercase tracking-widest items-center gap-2">
                <CheckCircle2 className="h-3 w-3" /> Selection Active
             </div>
          )}
        </div>
        
        {images.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 border-dashed p-12 sm:p-16 text-center">
            <p className="text-slate-400 text-sm sm:text-lg">No slides extracted yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
              {images.map(img => {
                  const isSelected = selectedIds.has(img.id);
                  const filterStyle = img.filters ? {
                    filter: `brightness(${img.filters.brightness}%) contrast(${img.filters.contrast}%) grayscale(${img.filters.grayscale ? 1 : 0}) invert(${img.filters.inverted ? 1 : 0})`
                  } : {};

                  return (
                    <div 
                      key={img.id} 
                      onClick={() => isSelectionMode ? toggleImageSelection(img.id) : setSelectedImageId(img.id)}
                      className={cn(
                        "group relative bg-white rounded-2xl sm:rounded-3xl border overflow-hidden shadow-sm transition-all duration-300 cursor-pointer",
                        isSelected ? "border-primary-500 ring-2 sm:ring-4 ring-primary-500/20 translate-y-[-2px] sm:translate-y-[-4px]" : "border-slate-200 hover:shadow-xl hover:-translate-y-1 sm:hover:-translate-y-2",
                        isSelectionMode ? "hover:scale-[1.01] active:scale-95" : "active:scale-98"
                      )}
                    >
                        <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                            <img 
                                src={img.image_url} 
                                alt={img.identified_organ_tissue} 
                                style={filterStyle}
                                className={cn(
                                  "w-full h-full object-cover transition-transform duration-700",
                                  !isSelectionMode && "group-hover:scale-110",
                                  isSelected && "scale-105"
                                )}
                            />
                            
                            {(img.annotations?.length || 0) > 0 && (
                                <div className="absolute bottom-2 left-2 px-1.5 py-0.5 bg-primary-600/90 text-[8px] font-black text-white rounded flex items-center gap-1 uppercase tracking-widest">
                                  <MapPin className="h-2 w-2" /> {img.annotations?.length} Arrows
                                </div>
                            )}

                            {isSelectionMode && (
                               <div className={cn(
                                 "absolute top-2 right-2 sm:top-3 sm:right-3 p-1 rounded-lg border transition-all shadow-lg",
                                 isSelected ? "bg-primary-500 border-primary-400 text-white" : "bg-white/80 backdrop-blur-md border-white/50 text-slate-400"
                               )}>
                                  {isSelected ? <CheckCircle2 className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                               </div>
                            )}

                            {!isSelectionMode && (
                              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                                   <div className="w-full bg-white text-slate-900 text-[9px] font-black uppercase tracking-widest py-2 rounded-lg shadow-xl flex items-center justify-center gap-1.5">
                                      <Search className="h-2.5 w-2.5" />
                                      Analyze Slide
                                   </div>
                              </div>
                            )}
                        </div>
                        <div className="p-3 sm:p-4 md:p-5">
                            <p className="font-bold text-slate-900 text-xs sm:text-sm md:text-base line-clamp-2 leading-tight min-h-[2.5em] mb-1">
                              {img.identified_organ_tissue}
                            </p>
                            <p className="text-[8px] sm:text-[10px] text-slate-400 truncate uppercase tracking-widest font-black opacity-80">
                              {img.original_filename}
                            </p>
                        </div>
                    </div>
                  );
              })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedImage && (
          <SlideModal 
            image={selectedImage} 
            onClose={() => setSelectedImageId(null)} 
          />
        )}
        {isBulkEditModalOpen && (
          <BulkEditModal 
            selectedCount={selectedIds.size} 
            onClose={() => setIsBulkEditModalOpen(false)} 
            onSave={handleBulkUpdate} 
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LibraryPage;